import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useMemo } from 'react';

interface StripeAuthData {
	authorised?: boolean;
	connected?: boolean;
	stripe_user_id?: string;
	stripeId?: string;
	access_token?: string;
	refresh_token?: string;
	account_id?: string;
	publishable_key?: string;
}

interface StripeConnectionStatus {
	isConnected: boolean;
	isAuthorized: boolean;
	hasStripeAccount: boolean;
	canProcessPayments: boolean;
	stripeUserId?: string;
	stripeId?: string;
	connectionType: 'none' | 'legacy' | 'connect' | 'full';
	authData?: StripeAuthData;
}

/**
 * Global hook to check if user has Stripe integration available
 * @returns {StripeConnectionStatus} Comprehensive Stripe connection status
 */
export const useStripeConnection = (): StripeConnectionStatus => {
	const user = useSelector(
		(state: DefaultRootState) => state.promiseData.data?.userData
	);

	return useMemo(() => {
		const stripeAuth: StripeAuthData | undefined =
			user?.authentications?.stripe;

		// If no Stripe auth data exists
		if (!stripeAuth) {
			return {
				isConnected: false,
				isAuthorized: false,
				hasStripeAccount: false,
				canProcessPayments: false,
				connectionType: 'none',
			};
		}

		// Check different connection patterns based on backend analysis
		const hasLegacyConnection = Boolean(
			stripeAuth.stripeId && stripeAuth.connected
		);
		const hasConnectAccount = Boolean(
			stripeAuth.stripe_user_id && stripeAuth.authorised
		);
		const hasAccessToken = Boolean(stripeAuth.access_token);

		// Determine connection type and capabilities
		let connectionType: 'none' | 'legacy' | 'connect' | 'full' = 'none';
		let canProcessPayments = false;

		if (hasConnectAccount && hasAccessToken) {
			connectionType = 'full';
			canProcessPayments = true;
		} else if (hasConnectAccount) {
			connectionType = 'connect';
			canProcessPayments = true;
		} else if (hasLegacyConnection) {
			connectionType = 'legacy';
			canProcessPayments = true;
		}

		const isConnected = hasLegacyConnection || hasConnectAccount;
		const isAuthorized = Boolean(
			stripeAuth.authorised || stripeAuth.connected
		);
		const hasStripeAccount = Boolean(
			stripeAuth.stripeId || stripeAuth.stripe_user_id
		);

		return {
			isConnected,
			isAuthorized,
			hasStripeAccount,
			canProcessPayments,
			stripeUserId: stripeAuth.stripe_user_id,
			stripeId: stripeAuth.stripeId,
			connectionType,
			authData: stripeAuth,
		};
	}, [user?.authentications?.stripe]);
};

/**
 * Simple hook to check if user is connected to Stripe (backward compatibility)
 * @returns {boolean} True if user has any valid Stripe connection
 */
export const useIsStripeConnected = (): boolean => {
	const { isConnected } = useStripeConnection();
	return isConnected;
};

/**
 * Hook to check if user can process payments through Stripe
 * @returns {boolean} True if user can process payments
 */
export const useCanProcessStripePayments = (): boolean => {
	const { canProcessPayments } = useStripeConnection();
	return canProcessPayments;
};

/**
 * Hook to get Stripe account identifier (prioritizes Connect account)
 * @returns {string | undefined} Stripe account ID
 */
export const useStripeAccountId = (): string | undefined => {
	const { stripeUserId, stripeId } = useStripeConnection();
	return stripeUserId || stripeId;
};

export default useStripeConnection;
